// @flow
import * as React from 'react';

import memoize from 'memoize-one';
import { connect } from 'react-redux';
import { superpathjoin } from 'superpathjoin';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectCartUUID as selectCartId } from '@gandi/cart';
import { notifyDanger, type NotifyAction } from '@gandi/notifications';
import { selectCurrentId } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';
import { Base, Button, Svg } from '@gandi/ui';
import { Action } from '@gandi/ui/deprecated';

import {
  addRenewableProductsToCart,
  fetchProductQuotations,
  fetchRenewableProducts,
  type FetchProductQuotations,
  type FetchProductQuotationsReturn,
} from 'src/DomainRenew/componentsNDS/modules/actions';
import { selectOtherRenewableProducts } from 'src/DomainRenew/componentsNDS/modules/selectors';

import type {
  ExtendedRenewableProduct,
  QuotedProduct,
} from 'src/DomainRenew/componentsNDS/types';

import { selectAdminURL } from '../../../modules/config';
import type { FullState } from '../../../types';

import FetchingRow from './FetchingRow';
import { LoginRowSwitch } from './LoginRowSwitch';
import { NoRenewablesRowSwitch } from './NoRenewablesRowSwitch';
import SubList from './SubList';

import styles from './MainList.scss';

const RENEWABLE_LIMIT = 20 as const;

type DispatchProps = {
  fetchRenewableProducts: () => Promise<any>,
  notifyDanger: NotifyAction,
  addRenewableProductsToCart: (
    translator: TranslatorProps,
    products: Array<ExtendedRenewableProduct>,
  ) => Promise<any>,
  fetchProductQuotations: FetchProductQuotations<FetchProductQuotationsReturn>,
};

type StateProps = {
  adminDomainUrl: string,
  authenticated: boolean,
  cartId: string,
  otherRenewableProducts: Array<ExtendedRenewableProduct>,
};

type OwnProps = {
  translator: TranslatorProps,
  onFetchRenewables?: (products: Array<ExtendedRenewableProduct>) => void,
  hidden?: boolean,
};

type Props = { ...DispatchProps, ...StateProps, ...OwnProps };

type State = {
  loading: boolean,
  loadingQuotations: boolean,
  loadingRenewables: boolean,
  renewablesLoaded: boolean,
  renewablesFailedToLoad: boolean,
  quotedProducts: Array<QuotedProduct>,
  ...
};

const getLimitedEntries = memoize((entries: Array<ExtendedRenewableProduct>) =>
  entries.slice(0, RENEWABLE_LIMIT),
);

class MainList extends React.PureComponent<Props, State> {
  state: State = {
    loading: false,
    loadingQuotations: false,
    loadingRenewables: false,
    renewablesLoaded: false,
    renewablesFailedToLoad: false,
    quotedProducts: [],
  };

  async componentDidMount() {
    const { authenticated, onFetchRenewables } = this.props;

    if (!authenticated) {
      return;
    }
    this.setState({ loadingQuotations: true, loadingRenewables: true });

    try {
      await this.props.fetchRenewableProducts();
      const { otherRenewableProducts } = this.props;
      if (onFetchRenewables) {
        onFetchRenewables(otherRenewableProducts);
      }
      if (otherRenewableProducts.length === 0) {
        this.setLoadingOver(true);
        return;
      }

      this.setState({
        loadingRenewables: false,
        renewablesLoaded: true,
      });

      this.props
        .fetchProductQuotations(getLimitedEntries(otherRenewableProducts))
        .catch(() => {} /* pass */)
        .then((quotedProducts: Array<QuotedProduct> = []) => {
          this.setState({ quotedProducts, loadingQuotations: false });
        });
    } catch (e) {
      this.setLoadingOver(false);
    }
  }

  setLoadingOver(success: boolean) {
    this.setState({
      loadingQuotations: false,
      loadingRenewables: false,
      renewablesLoaded: success,
      renewablesFailedToLoad: !success,
    });
  }

  handleAddAllToCartClick: (evt: SyntheticMouseEvent<>) => Promise<void> =
    async (evt: SyntheticMouseEvent<>) => {
      evt.preventDefault();

      if (this.state.loading) {
        return;
      }

      const {
        otherRenewableProducts,
        translator: { __ },
      } = this.props;

      this.setState({ loading: true });

      try {
        await this.props.addRenewableProductsToCart(
          this.props.translator,
          otherRenewableProducts,
        );
      } catch (err) {
        this.props.notifyDanger(__('Unable to add the products in the cart.'));
      }

      this.setState({ loading: false });
    };

  renderCartCallToAction(allRenewablesAlreadyInCart: boolean): React.Node {
    const {
      translator: { __ },
      cartId,
    } = this.props;

    return (
      <Base
        padding="xsmall"
        backgroundColor="white"
        flex
        flexRowReverse
        className={styles.cartCallToAction}
      >
        {allRenewablesAlreadyInCart ? (
          <Button
            small
            type="button"
            disabled={this.state.loading}
            to={this.state.loading ? undefined : `/cart/${cartId}/overview`}
          >
            {__('Checkout')}
          </Button>
        ) : (
          <Button
            small
            type="button"
            disabled={this.state.loading}
            onClick={this.handleAddAllToCartClick}
          >
            {__('Add all to cart')}
          </Button>
        )}
      </Base>
    );
  }

  renderAdminCallToAction(): React.Node {
    const {
      translator: { __ },
      adminDomainUrl,
    } = this.props;

    return (
      <Action
        href={adminDomainUrl}
        icon="arrowRight"
        variant="block"
        marginTop="base"
      >
        {__('Manage from Dashboard')}
      </Action>
    );
  }

  renderProductList: (
    entries: Array<ExtendedRenewableProduct>,
    index: number,
  ) => null | React.Node = (
    entries: Array<ExtendedRenewableProduct>,
    index: number,
  ) =>
    entries.length > 0 ? (
      <SubList
        key={index}
        entries={entries}
        loading={this.state.loading}
        loadingQuotations={this.state.loadingQuotations}
        productType={entries[0].type}
        quotedProducts={this.state.quotedProducts.filter(product =>
          entries[0].type === 'simple-hosting'
            ? product.type === 'paas'
            : product.type === entries[0].type,
        )}
        translator={this.props.translator}
        notifyDanger={this.props.notifyDanger}
        addRenewableProductsToCart={this.props.addRenewableProductsToCart}
      />
    ) : null;

  render(): null | React.Node {
    const {
      adminDomainUrl,
      authenticated,
      translator,
      translator: { __ },
      otherRenewableProducts,
      hidden,
    } = this.props;

    const { loadingRenewables, renewablesLoaded, renewablesFailedToLoad } =
      this.state;

    if (hidden || renewablesFailedToLoad) return null;

    if (!authenticated) return <LoginRowSwitch />;

    if (!renewablesLoaded && !loadingRenewables) return null;

    if (loadingRenewables) return <FetchingRow translator={translator} />;

    if (!otherRenewableProducts.length) return <NoRenewablesRowSwitch />;

    const limitedEntries = getLimitedEntries(otherRenewableProducts);

    const sortedEntries = [
      limitedEntries.filter(product => product.type === 'domain'),
      limitedEntries.filter(product => product.type === 'simple-hosting'),
    ];

    const allRenewablesAlreadyInCart = otherRenewableProducts.every(
      entry => !!entry.product,
    );
    return (
      <>
        <Base flex flexAlignItemsCenter paddingBottom="xsmall">
          <Base tagName="h3" flex flewGrow1 margin="none" marginRight="small">
            <Svg className={styles.icon} name="bell" />
            {otherRenewableProducts.length > RENEWABLE_LIMIT
              ? __('A lot of products are expiring, including')
              : __('You should also renew these products')}
          </Base>
          {!allRenewablesAlreadyInCart &&
            otherRenewableProducts.length <= RENEWABLE_LIMIT && (
              <Base
                tagName="a"
                disabled={this.state.loading}
                onClick={this.handleAddAllToCartClick}
                role="button"
                style={{ cursor: 'pointer' }}
              >
                {__('Add all to cart')}
              </Base>
            )}
          {otherRenewableProducts.length > RENEWABLE_LIMIT && (
            <Button
              tagName="a"
              disabled={this.state.loading}
              href={this.state.loading ? undefined : adminDomainUrl}
            >
              {__('View all in dashboard')}{' '}
              <Svg name="arrowRight" theme="info" className={styles.icon} />
            </Button>
          )}
        </Base>

        {sortedEntries.map(this.renderProductList)}
        {otherRenewableProducts.length > RENEWABLE_LIMIT
          ? this.renderAdminCallToAction()
          : this.renderCartCallToAction(allRenewablesAlreadyInCart)}
      </>
    );
  }
}

const mapStateToProps = (state: FullState): StateProps => {
  const sharingId = selectCurrentId(state);
  const adminDomainUrl = superpathjoin(
    selectAdminURL(state),
    'domain',
    sharingId,
  );

  return {
    adminDomainUrl,
    authenticated: selectIsAuthenticatedUser(state),
    cartId: selectCartId(state),
    otherRenewableProducts: selectOtherRenewableProducts(state),
  };
};

const mapDispatchToProps = {
  notifyDanger,
  addRenewableProductsToCart,
  fetchProductQuotations,
  fetchRenewableProducts,
};

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export const RenewableProducts = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MainList);
